import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import Snackbar from './Snackbar';
import './AdminUserManagement.css';

const AdminUserManagement = ({ user, onShowNotification }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('success');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  useEffect(() => {
    fetchUsers();
  }, [currentPage, roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const url = new URL(getApiUrl('/api/admin/all-users'));
      url.searchParams.append('page', currentPage);
      url.searchParams.append('limit', '20');
      if (roleFilter !== 'all') {
        url.searchParams.append('role', roleFilter);
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch admin users');
      }

      const data = await response.json();
      setUsers(data.users || []);
      setPagination(data.pagination || { total: 0, totalPages: 1 });
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbarMessage('Failed to fetch admin users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbarMessage = (message, type = 'success') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setShowSnackbar(true);
    if (onShowNotification) {
      onShowNotification(message, type);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/admin/all-users/${userId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user status');
      }

      showSnackbarMessage(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      showSnackbarMessage(error.message || 'Failed to update user status', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const badges = {
      'super_admin': { label: 'Super Admin', class: 'badge-super' },
      'finance_admin': { label: 'Finance Admin', class: 'badge-finance' },
      'admin': { label: 'Admin', class: 'badge-admin' }
    };
    return badges[role] || { label: role, class: 'badge-default' };
  };

  return (
    <div className="admin-user-management">
      <div className="management-header">
        <div>
          <h2>Admin User Management</h2>
          <p>Manage all admin users (Super Admins, Finance Admins, Admins)</p>
        </div>
        <div className="header-actions">
          <select
            className="role-filter"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="finance_admin">Finance Admin</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading admin users...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <p>No admin users found.</p>
        </div>
      ) : (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((adminUser) => {
                  const roleBadge = getRoleBadge(adminUser.role);
                  return (
                    <tr key={adminUser.id}>
                      <td>
                        <div className="user-info">
                          <span className="user-name">{adminUser.first_name} {adminUser.last_name}</span>
                        </div>
                      </td>
                      <td>{adminUser.username}</td>
                      <td>{adminUser.email || 'N/A'}</td>
                      <td>
                        <span className={`badge ${roleBadge.class}`}>
                          {roleBadge.label}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${adminUser.is_active ? 'active' : 'inactive'}`}>
                          {adminUser.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{formatDate(adminUser.last_login)}</td>
                      <td>{formatDate(adminUser.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleToggleStatus(adminUser.id, adminUser.is_active)}
                            className={`toggle-status-btn ${adminUser.is_active ? 'deactivate' : 'activate'}`}
                            disabled={adminUser.id === user?.id && !adminUser.is_active}
                            title={adminUser.id === user?.id && !adminUser.is_active ? 'Cannot deactivate yourself' : adminUser.is_active ? 'Deactivate user' : 'Activate user'}
                          >
                            {adminUser.is_active ? '🚫 Deactivate' : '✅ Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {pagination.totalPages} ({pagination.total} total users)
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={currentPage === pagination.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default AdminUserManagement;

